import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { IDataflowTypeMetadataSource } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { RestBasedServiceAuthenticationType } from 'shell-models-lib';

const controls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'url',
            label: stringResources.Database_URL,
            required: true,
            placeholder: format(stringResources.Example, 'https://www.teamdesk.net/secure/db/57692'),
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            warningMessageCalculator: (value: string) =>
                stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning,
            isLabelVertical: true
        },
        warningMessageCalculator: (value: string) =>
            stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning
    },
    {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        required: true,
        options: [
            {
                label: stringResources.Basic_DisplayText,
                value: RestBasedServiceAuthenticationType.BASIC
            },
            {
                label: stringResources.ModelName_Token,
                value: RestBasedServiceAuthenticationType.TOKEN
            }
        ],
        isLabelVertical: true,
        defaultValue: RestBasedServiceAuthenticationType.BASIC,
        cases: [
            {
                caseValue: RestBasedServiceAuthenticationType.BASIC,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'userName',
                            label: stringResources.ModelName_UserName,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        hideAKVSwitcherInConnStrProps: true,
                        bind: 'password',
                        label: stringResources.ModelName_Password,
                        child: {
                            type: 'parameterizable',
                            attribute: 'password',
                            disableAddParameter: true,
                            child:
                                {
                                    type: 'password',
                                    label: stringResources.ModelName_Password,
                                    required: true,
                                    isLabelVertical: true,
                                    validators: [
                                        { type: 'required', errorMessage: '' }
                                    ]
                                }
                        },
                        description: format(
                            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.TeamDesk_DisplayText),
                            stringResources.ModelName_Password
                        )
                    }
                ]
            },
            {
                caseValue: RestBasedServiceAuthenticationType.TOKEN,
                children: [
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        hideAKVSwitcherInConnStrProps: true,
                        bind: 'apiToken',
                        label: stringResources.ModelName_Token,
                        child: {
                            type: 'parameterizable',
                            attribute: 'apiToken',
                            disableAddParameter: true,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_Token,
                                required: true,
                                isLabelVertical: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        },
                        description: format(
                            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.TeamDesk_DisplayText),
                            stringResources.ModelName_Token
                        )
                    }
                ]
            }
        ]
    }
];

const dataflowTeamDeskSource: IDataflowTypeMetadataSource = {
    type: DatasetFormatType.TeamDesk,
    displayName: stringResources.TeamDesk_DisplayText,
    settings: adfModelTypeMap.teamDesk.dataflowSettings,
    controls: [
        {
            type: 'dataflow-schema-table-selection',
            bind: {
                schemaName: 'schemaName',
                tableName: 'tableName'
            },
            schemaNameLabel: stringResources.ModelName_Table,
            schemaNamePlaceholder: stringResources.DataFlowHiveLinkedServiceRefreshTablePlaceholder,
            tableNameLabel: stringResources.ModelName_View,
            tableNamePlaceholder: stringResources.DataFlowLinkedServiceSelectSubViewPlaceholder,
            schemaRequired: true,
            selectionType: ['schema', 'table']
        }
    ],
    useInlineProjection: true
};

export const teamDeskMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.teamDesk.linkedServiceType,
    displayName: format(stringResources.ModelName_DisplayTextPreviewTemplate, stringResources.TeamDesk_DisplayText),
    linkedServiceKind: 'unknown',
    icon: {
        className: 'svg-i-100x100-teamdesk'
    },
    category: ['ServicesApps'],
    onlyAzureIR: true,
    copySourceTypes: [],
    copySinkTypes: [],
    isFirstClass: true,
    supportDataFlowSource: true,
    dataflowSource: [dataflowTeamDeskSource],
    supportFirstClassParameterExperience: true,
    controls
};
